import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ImageOff, PackageCheck, ShoppingCart, Trash2, User, AlertTriangle, Check } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { Select } from '@/components/form/Select';
import { FormGroup } from '@/components/form/FormGroup';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { useConfirm } from '@/components/ui/useConfirm';
import { useCart } from '@/cart/useCart';
import { useAuth } from '@/auth/useAuth';
import { listParties, type Party } from '@/api/parties';
import { listPriceMaster } from '@/api/price-master';
import { createEnquiry, type Priority } from '@/api/enquiry';
import { formatINR, formatINRSymbol, todayISO } from '@/lib/format';
import { apiError } from '@/lib/apiError';
import { effectiveRate, rateToDiscountAmt } from './rateEdit';
import { listHsnGstBands } from '@/api/hsnGst';
import { gstAddedOnTop, gstSplit, inclusiveFromExRate } from '@/lib/gstSplit';

const PRIORITIES: Priority[] = ['Low', 'Normal', 'High', 'Urgent'];

export function OrderSummaryPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const confirmDlg = useConfirm();
  const qc = useQueryClient();
  const { user } = useAuth();
  const cart = useCart();

  const [partyId, setPartyId] = useState<number | null>(null);
  const [customerPoRef, setCustomerPoRef] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [priority, setPriority] = useState<Priority>('Normal');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [remarks, setRemarks] = useState('');
  // When on, the GST/Pc column is shown (MRP is GST-inclusive — the amount never changes).
  const [gstOnRatePc, setGstOnRatePc] = useState(false);
  // One-time order-level special discount (CD / Final Sale).
  const [specialDiscType, setSpecialDiscType] = useState<'amount' | 'pct'>('amount');
  const [specialDiscValue, setSpecialDiscValue] = useState('');
  const [specialDiscLabel, setSpecialDiscLabel] = useState('CD');
  // Typed Rate/Pc (ex-GST, as the Sales Order prints it) per cart line —
  // Corporate & Projects only, keyed by cart line id.
  const [rateEdits, setRateEdits] = useState<Record<number, string>>({});

  // Only Customers — Leads are not allowed to place firm orders.
  const partiesQuery = useQuery({
    queryKey: ['parties', 'customers-only'],
    queryFn: () => listParties({ type: 'customer', pageSize: 10000 }),
  });

  const customers: Party[] = useMemo(
    () => (partiesQuery.data?.data ?? []).filter((p) => p.isCustomer && p.customerType !== 'Lead'),
    [partiesQuery.data],
  );

  const partyOptions: SearchableOption[] = useMemo(
    () => customers.map((p) => ({ value: p.id, label: p.name, sublabel: p.code })),
    [customers],
  );

  const selectedParty = useMemo(
    () => customers.find((p) => p.id === partyId) ?? null,
    [customers, partyId],
  );

  // Auto-fill delivery address from Party Master whenever the picked customer
  // changes. Overwrites any prior text — after switching customer, the user
  // sees the new customer's address (which they can still edit).
  useEffect(() => {
    if (!selectedParty) {
      setDeliveryAddress('');
      return;
    }
    const parts = [
      selectedParty.addressLine1,
      selectedParty.addressLine2,
      selectedParty.city,
      selectedParty.state?.name ?? selectedParty.stateCode,
      selectedParty.pin,
      selectedParty.country,
    ].filter(Boolean);
    setDeliveryAddress(parts.join(', '));
  }, [selectedParty]);

  // Typed rates belong to the customer they were typed for.
  useEffect(() => setRateEdits({}), [partyId]);
  const canEditRate = selectedParty?.customerType === 'Corporate & Projects';

  // Customer discount cascade — the SAME tiers the Sales Order document applies:
  // Price Master (trade + special, by customer type) + Friends & Family (per
  // party), ADDED together and applied to MRP. This drives the Rate/Pc preview.
  const pmQuery = useQuery({
    queryKey: ['price-master', selectedParty?.customerType ?? null],
    queryFn: () => listPriceMaster({ priceList: selectedParty?.customerType ?? undefined, status: 'Active' }),
    enabled: !!selectedParty?.customerType,
  });
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const discountPct = useMemo(() => {
    const pm = pmQuery.data?.data?.[0];
    const trade = Number(pm?.discount ?? 0);
    const special = Number(pm?.specialDiscount ?? 0);
    const ff = Number(selectedParty?.friendsFamilyDiscount ?? 0);
    return trade + special + ff;
  }, [pmQuery.data, selectedParty]);
  const netFactor = Math.max(0, 1 - discountPct / 100);

  const items = cart.cart?.items ?? [];
  // HSN GST master bands — GST% per line is picked from these by price, as on the Sales Order.
  const bandsQuery = useQuery({ queryKey: ['hsn-gst', 'bands'], queryFn: listHsnGstBands, staleTime: 60_000 });
  type Line = (typeof items)[number];
  // Corporate & Projects + "GST Extra on Rate/PC": the discounted rate is ex-GST
  // and GST is added on top. Everyone else: MRP already includes GST.
  const gstOnTop = gstAddedOnTop(gstOnRatePc, selectedParty?.customerType);
  // Per-pc discounted MRP: MRP after the discount cascade, or the typed Rate/Pc
  // (Corporate & Projects) turned back into it. GST-inclusive unless gstOnTop.
  const tierRate = (l: Line) => round2(l.item.mrp * netFactor);
  const inclRate = (l: Line) => {
    const typed = canEditRate ? rateEdits[l.id] : undefined;
    if (typed == null || typed.trim() === '') return tierRate(l);
    const incl = gstOnTop ? Number(typed) : inclusiveFromExRate(Number(typed), l.item.hsn, l.item.gstPct, bandsQuery.data);
    return effectiveRate(l.item.mrp, tierRate(l), String(incl));
  };
  // Rate/Pc, GST/Pc and the line amount exactly as the Sales Order prints them.
  const split = (l: Line) => gstSplit(round2(inclRate(l) * l.qty), l.qty, l.item.hsn, l.item.gstPct, bandsQuery.data, gstOnTop);
  const lineTotal = (l: Line) => split(l).amount;
  const lineDiscPct = (l: Line) => (l.item.mrp > 0 ? (1 - inclRate(l) / l.item.mrp) * 100 : discountPct);
  const subtotal = round2(items.reduce((s, l) => s + lineTotal(l), 0));
  // One-time special discount → rupee amount, clamped to the subtotal.
  const specialDiscNum = Math.max(0, Number(specialDiscValue) || 0);
  const specialDiscAmt = round2(
    specialDiscNum <= 0 ? 0
      : specialDiscType === 'pct' ? (subtotal * Math.min(100, specialDiscNum)) / 100
      : Math.min(specialDiscNum, subtotal),
  );
  const grandTotal = round2(subtotal - specialDiscAmt);

  const creditLimit = Number(selectedParty?.creditLimit ?? 0);
  const outstanding = Number(selectedParty?.outstanding ?? 0);
  const available = creditLimit - outstanding;
  const wouldExceed = selectedParty ? outstanding + grandTotal > creditLimit && creditLimit > 0 : false;

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!selectedParty) throw new Error('Pick a customer first');
      if (items.length === 0) throw new Error('Cart is empty');
      return createEnquiry({
        enqDate: todayISO(),
        partyId: selectedParty.id,
        deliveryAddress: deliveryAddress.trim() || undefined,
        customerPoRef: customerPoRef.trim() || undefined,
        expectedDelivery: expectedDelivery || undefined,
        priority,
        salesRep: user?.fullName ?? 'Sales',
        source: 'Direct Order',
        remarks: remarks.trim() || undefined,
        flowType: 'order',
        gstOnRatePc,
        specialDiscount: specialDiscNum > 0 ? specialDiscNum : undefined,
        specialDiscountType: specialDiscNum > 0 ? specialDiscType : undefined,
        specialDiscountLabel: specialDiscNum > 0 ? (specialDiscLabel.trim() || undefined) : undefined,
        items: items.map((l) => ({
          itemId: l.itemId,
          itemCode: l.item.code,
          itemName: l.item.name,
          hsn: l.item.hsn ?? undefined,
          gstPct: l.item.gstPct,
          qty: l.qty,
          unit: l.item.unit,
          // Send MRP — the Sales Order applies the discount cascade from the
          // master MRP at render time (same as orders from the Enquiry form).
          rate: l.item.mrp,
          // A typed Rate/Pc travels as the line's ₹ discount off MRP.
          discountAmt: canEditRate ? rateToDiscountAmt(l.item.mrp, l.qty, inclRate(l), tierRate(l)) : undefined,
        })),
      });
    },
    onSuccess: async (order) => {
      await cart.clear();
      qc.invalidateQueries({ queryKey: ['enquiries'] });
      toast.pushStep(`Order ${order.enqNo} created`, {
        body: 'Sent to Order Review for confirmation.',
        href: `/sales/enq-review?highlight=${order.id}`,
        nextLabel: 'Open Order Review',
        tag: `order-summary-${order.id}`,
      });
      navigate(`/sales/enq-review?highlight=${order.id}`);
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'Could not place order'), 'error');
    },
  });

  async function onConfirm() {
    if (!selectedParty) {
      toast.push('Pick a customer first', 'error');
      return;
    }
    if (items.length === 0) {
      toast.push('Cart is empty — add items first', 'error');
      return;
    }
    if (wouldExceed) {
      const proceed = await confirmDlg(
        `This order would exceed the customer's credit limit.\n` +
          `Limit: ${formatINRSymbol(creditLimit)}\n` +
          `Outstanding: ${formatINRSymbol(outstanding)}\n` +
          `This order: ${formatINRSymbol(grandTotal)}\n\n` +
          `Backend will still accept but flag it as credit-pending. Continue?`,
      );
      if (!proceed) return;
    }
    submitMutation.mutate();
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="glass" size="sm" onClick={() => navigate('/sales/catalogue')}>
            <span className="inline-flex items-center gap-1"><ArrowLeft size={13} /> Back to Catalogue</span>
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <PackageCheck size={18} className="text-accent" />
          <div>
            <div className="text-lg font-black text-[var(--text-primary)]">Place Order</div>
            <div className="text-[11px] text-[var(--text-muted)]">Firm order — routed through Order Review (auto-raises MRN for any shortfall) before Dispatch Order.</div>
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[var(--border-color)] p-8 text-center">
          <ShoppingCart size={40} className="mx-auto opacity-30" />
          <div className="mt-3 text-sm font-semibold text-[var(--text-secondary)]">Your cart is empty</div>
          <div className="mt-1 text-xs text-[var(--text-muted)]">Add items from the Product Catalogue to place an order.</div>
          <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/sales/catalogue')}>
            Go to Catalogue
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-3">
          {/* LEFT: order details */}
          <div className="space-y-4 lg:col-span-2">
            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
              <div className="mb-3 flex items-center gap-2 text-sm font-extrabold text-[var(--text-primary)]">
                <User size={14} className="text-accent" /> Customer & delivery
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <FormGroup label="Customer *" required>
                  <SearchableSelect
                    value={partyId ?? ''}
                    onChange={(v) => setPartyId(typeof v === 'number' ? v : null)}
                    options={partyOptions}
                    placeholder={partiesQuery.isLoading ? 'Loading customers…' : 'Search customer…'}
                    disabled={partiesQuery.isLoading}
                  />
                </FormGroup>
                <FormGroup label="Priority">
                  <Select value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
                    {PRIORITIES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </Select>
                </FormGroup>
                <FormGroup label="Customer PO / Reference">
                  <Input
                    type="text"
                    maxLength={60}
                    value={customerPoRef}
                    onChange={(e) => setCustomerPoRef(e.target.value)}
                    placeholder="Their PO number (optional)"
                  />
                </FormGroup>
                <FormGroup label="Requested delivery date">
                  <Input
                    type="date"
                    value={expectedDelivery}
                    onChange={(e) => setExpectedDelivery(e.target.value)}
                    min={todayISO()}
                  />
                </FormGroup>
                <FormGroup label="Delivery Address" className="md:col-span-2">
                  <Textarea
                    rows={2}
                    value={deliveryAddress}
                    onChange={(e) => setDeliveryAddress(e.target.value)}
                    placeholder={selectedParty
                      ? 'Auto-filled from Party Master — edit if goods ship elsewhere'
                      : 'Pick a customer to auto-fill'}
                  />
                </FormGroup>
                <FormGroup label="Remarks" className="md:col-span-2">
                  <Textarea
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Any note for the sales / warehouse team"
                    maxLength={4000}
                  />
                </FormGroup>
              </div>
            </div>

            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
              <div className="mb-3 flex items-center justify-between">
                <div className="text-sm font-extrabold text-[var(--text-primary)]">
                  Line items · {items.length}
                </div>
                <Button
                  variant="glass"
                  size="sm"
                  onClick={() => cart.clear()}
                  title="Empty the cart"
                >
                  <span className="inline-flex items-center gap-1"><Trash2 size={12} /> Clear cart</span>
                </Button>
              </div>
              <label className="mb-2 flex w-fit cursor-pointer items-center gap-2 text-xs font-semibold text-[var(--text-secondary)]">
                <input
                  type="checkbox"
                  checked={gstOnRatePc}
                  onChange={(e) => setGstOnRatePc(e.target.checked)}
                  className="h-4 w-4 accent-accent"
                />
                GST Extra on Rate/PC
              </label>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
                      <th className="px-2 py-1.5">Item</th>
                      <th className="px-2 py-1.5">Code</th>
                      <th className="px-2 py-1.5">Barcode</th>
                      <th className="px-2 py-1.5 text-right">Qty</th>
                      <th className="px-2 py-1.5">Unit</th>
                      <th className="px-2 py-1.5 text-right">MRP</th>
                      <th className="px-2 py-1.5 text-right">Disc %</th>
                      <th className="px-2 py-1.5 text-right">Rate/Pc</th>
                      {gstOnRatePc && <th className="px-2 py-1.5 text-right">GST/Pc</th>}
                      <th className="px-2 py-1.5 text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((l) => (
                      <tr key={l.id} className="border-t border-[var(--border-color)]">
                        <td className="px-2 py-1.5 font-semibold">
                          <div className="flex items-center gap-2">
                            {l.item.imageUrl ? (
                              <img src={l.item.imageUrl} alt="" loading="lazy" className="h-8 w-8 shrink-0 rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] object-cover" />
                            ) : (
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-muted)]"><ImageOff size={13} /></span>
                            )}
                            <span className="min-w-0">{l.item.name}</span>
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-[var(--text-muted)]">{l.item.code}</td>
                        <td className="px-2 py-1.5 font-mono font-bold text-[var(--text-primary)]">{l.item.barcode ?? '—'}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{l.qty}</td>
                        <td className="px-2 py-1.5">{l.item.unit}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-[var(--text-muted)]">{formatINR(l.item.mrp)}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{lineDiscPct(l) > 0.05 ? `${Math.round(lineDiscPct(l) * 10) / 10}%` : '—'}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-semibold">
                          {canEditRate ? (
                            <input
                              type="number"
                              min={0}
                              max={l.item.mrp}
                              step="0.01"
                              value={rateEdits[l.id] ?? String(split(l).ratePc)}
                              onChange={(e) => setRateEdits((m) => ({ ...m, [l.id]: e.target.value }))}
                              onBlur={() => setRateEdits((m) => (m[l.id] == null ? m : { ...m, [l.id]: String(split(l).ratePc) }))}
                              title="Rate per piece without GST, as on the Sales Order (price incl. GST up to MRP)"
                              aria-label={`Rate per piece for ${l.item.name}`}
                              className="w-24 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 text-right font-mono text-xs font-semibold"
                            />
                          ) : (
                            formatINR(split(l).ratePc)
                          )}
                        </td>
                        {gstOnRatePc && (
                          <td className="px-2 py-1.5 text-right font-mono text-[var(--text-secondary)]">
                            {formatINR(split(l).gstPerPc)} <span className="text-[9px]">({split(l).gstPct}%)</span>
                          </td>
                        )}
                        <td className="px-2 py-1.5 text-right font-bold">{formatINRSymbol(lineTotal(l))}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-[var(--border-color)]">
                      <td colSpan={gstOnRatePc ? 9 : 8} className="px-2 py-1.5 text-right text-xs font-semibold text-[var(--text-muted)]">Subtotal</td>
                      <td className="px-2 py-1.5 text-right text-sm font-bold">{formatINRSymbol(subtotal)}</td>
                    </tr>
                    <tr>
                      <td colSpan={gstOnRatePc ? 9 : 8} className="px-2 py-1.5 text-right">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <span className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Special discount</span>
                          <input
                            value={specialDiscLabel}
                            onChange={(e) => setSpecialDiscLabel(e.target.value)}
                            placeholder="Label — CD / Final Sale"
                            className="w-36 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 text-xs"
                          />
                          <select
                            value={specialDiscType}
                            onChange={(e) => setSpecialDiscType(e.target.value as 'amount' | 'pct')}
                            className="rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 py-1 text-xs"
                          >
                            <option value="amount">₹</option>
                            <option value="pct">%</option>
                          </select>
                          <input
                            type="number"
                            min={0}
                            value={specialDiscValue}
                            onChange={(e) => setSpecialDiscValue(e.target.value)}
                            placeholder="0"
                            className="w-20 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 text-right text-xs"
                          />
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-right text-sm font-bold text-danger">
                        {specialDiscAmt > 0 ? `− ${formatINRSymbol(specialDiscAmt)}` : '—'}
                      </td>
                    </tr>
                    <tr className="border-t border-[var(--border-color)]">
                      <td colSpan={gstOnRatePc ? 9 : 8} className="px-2 py-2 text-right text-xs font-semibold">
                        Grand total (incl. GST)
                      </td>
                      <td className="px-2 py-2 text-right text-base font-black text-accent">
                        {formatINRSymbol(grandTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* RIGHT: credit + confirm */}
          <div className="space-y-4">
            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
              <div className="mb-3 text-sm font-extrabold text-[var(--text-primary)]">Credit check</div>
              {!selectedParty ? (
                <div className="text-xs text-[var(--text-muted)]">Pick a customer to see credit balance.</div>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Credit limit</span>
                    <span className="font-semibold">{formatINRSymbol(creditLimit)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">Current outstanding</span>
                    <span className="font-semibold">{formatINRSymbol(outstanding)}</span>
                  </div>
                  <div className="flex justify-between border-t border-[var(--border-color)] pt-2">
                    <span className="text-[var(--text-muted)]">Available before order</span>
                    <span className={`font-black ${available >= 0 ? 'text-success' : 'text-danger'}`}>{formatINRSymbol(available)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[var(--text-muted)]">This order</span>
                    <span className="font-semibold">{formatINRSymbol(grandTotal)}</span>
                  </div>
                  <div className={`mt-2 rounded-md px-2 py-1.5 text-center font-black ${wouldExceed ? 'bg-danger/15 text-danger' : 'bg-success/15 text-success'}`}>
                    {wouldExceed ? <><AlertTriangle size={13} className="mr-1 inline-block align-[-2px]" /> Would exceed credit limit</> : <><Check size={13} className="mr-1 inline-block align-[-2px]" /> Within credit limit</>}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-4">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
                On confirm
              </div>
              <ul className="mb-3 space-y-1 text-[11px] text-[var(--text-secondary)]">
                <li>1. Order recorded as <strong>Sales Order</strong> ({items.length} line{items.length === 1 ? '' : 's'}).</li>
                <li>2. Lands in <strong>Order Review</strong> so stock is verified and MRN auto-raises for any shortfall.</li>
                <li>3. After review, moves to Dispatch Order → Pick → Pack → Ship.</li>
                <li>4. Cart is cleared. Print Sales Order PDF anytime from the Enquiry list.</li>
              </ul>
              <Button
                variant="primary"
                size="md"
                onClick={onConfirm}
                disabled={submitMutation.isPending || !selectedParty}
                className="w-full"
              >
                <span className="inline-flex items-center gap-1">
                  <PackageCheck size={14} />
                  {submitMutation.isPending ? 'Placing order…' : 'Confirm Order'}
                </span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
